import React, { useEffect, useState } from 'react';
import { X, User, Mail, Star, Tag, List, FileText, MapPin } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { Contact } from '../data/workspaceData';
interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  contact?: Contact | null; // If provided, we're editing; otherwise creating
}
export function ContactModal({
  isOpen,
  onClose,
  contact
}: ContactModalProps) {
  const {
    currentWorkspace,
    currentSubRegion,
    newsletters,
    tags,
    createContact,
    updateContact
  } = useWorkspace();
  const isEditing = !!contact;
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    category: '',
    optInStatus: 'pending' as 'pending' | 'confirmed' | 'unsubscribed',
    selectedTags: [] as string[],
    subscribedLists: [] as number[],
    source: 'manual' as 'form' | 'import' | 'manual',
    notes: '',
    subRegion: '',
    isHighProfile: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Reset form when modal opens/closes or contact changes
  useEffect(() => {
    if (isOpen) {
      if (contact) {
        setFormData({
          email: contact.email,
          firstName: contact.firstName || '',
          lastName: contact.lastName || '',
          category: contact.category || '',
          optInStatus: contact.optInStatus,
          selectedTags: contact.tags || [],
          subscribedLists: contact.subscribedLists || [],
          source: contact.source,
          notes: contact.notes || '',
          subRegion: contact.subRegion || '',
          isHighProfile: contact.isHighProfile
        });
      } else {
        setFormData({
          email: '',
          firstName: '',
          lastName: '',
          category: '',
          optInStatus: 'pending',
          selectedTags: [],
          subscribedLists: [],
          source: 'manual',
          notes: '',
          subRegion: currentSubRegion || '',
          isHighProfile: false
        });
      }
      setErrors({});
    }
  }, [isOpen, contact, currentSubRegion]);
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    const contactData = {
      workspaceId: currentWorkspace.id,
      subRegion: formData.subRegion || undefined,
      email: formData.email.trim(),
      firstName: formData.firstName.trim() || undefined,
      lastName: formData.lastName.trim() || undefined,
      category: formData.category || undefined,
      isHighProfile: !!formData.category,
      tags: formData.selectedTags,
      optInStatus: formData.optInStatus,
      optInConfirmedAt: formData.optInStatus === 'confirmed' ? new Date().toISOString().split('T')[0] : undefined,
      subscribedLists: formData.subscribedLists,
      source: formData.source,
      notes: formData.notes.trim() || undefined
    };
    if (isEditing && contact) {
      updateContact(contact.id, contactData);
    } else {
      createContact(contactData);
    }
    onClose();
  };
  const handleTagToggle = (tagName: string) => {
    setFormData(prev => ({
      ...prev,
      selectedTags: prev.selectedTags.includes(tagName) ? prev.selectedTags.filter(t => t !== tagName) : [...prev.selectedTags, tagName]
    }));
  };
  const handleListToggle = (listId: number) => {
    setFormData(prev => ({
      ...prev,
      subscribedLists: prev.subscribedLists.includes(listId) ? prev.subscribedLists.filter(id => id !== listId) : [...prev.subscribedLists, listId]
    }));
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-106">
      <div className="flex min-h-screen items-center justify-center p-4" data-id="element-107">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} data-id="element-108" />

        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-id="element-109">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0" data-id="element-110">
            <div className="flex items-center gap-3" data-id="element-111">
              <div className="p-2 bg-blue-50 rounded-lg" data-id="element-112">
                <User className="h-5 w-5 text-blue-600" data-id="element-113" />
              </div>
              <div data-id="element-114">
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-115">
                  {isEditing ? 'Edit Contact' : 'Add New Contact'}
                </h2>
                <p className="text-sm text-gray-500" data-id="element-116">
                  {isEditing ? 'Update contact information' : 'Create a new contact manually'}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500 flex-shrink-0" data-id="element-117">
              <X className="h-5 w-5" data-id="element-118" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto" data-id="element-119">
            <div className="px-6 py-4 space-y-6" data-id="element-120">
              {/* Basic Info */}
              <div className="space-y-4" data-id="element-121">
                <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-122">
                  <Mail className="h-4 w-4 text-gray-400" data-id="element-123" />
                  Basic Information
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-124">
                  <div className="sm:col-span-2" data-id="element-125">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-126">
                      Email Address <span className="text-red-500" data-id="element-127">*</span>
                    </label>
                    <input type="email" value={formData.email} onChange={e => setFormData(prev => ({
                    ...prev,
                    email: e.target.value
                  }))} className={`block w-full rounded-md shadow-sm sm:text-sm ${errors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'}`} placeholder="contact@example.com" data-id="element-128" />
                    {errors.email && <p className="mt-1 text-sm text-red-600" data-id="element-129">
                        {errors.email}
                      </p>}
                  </div>

                  <div data-id="element-130">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-131">
                      First Name
                    </label>
                    <input type="text" value={formData.firstName} onChange={e => setFormData(prev => ({
                    ...prev,
                    firstName: e.target.value
                  }))} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" placeholder="John" data-id="element-132" />
                  </div>

                  <div data-id="element-133">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-134">
                      Last Name
                    </label>
                    <input type="text" value={formData.lastName} onChange={e => setFormData(prev => ({
                    ...prev,
                    lastName: e.target.value
                  }))} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" placeholder="Doe" data-id="element-135" />
                  </div>
                </div>
              </div>

              {/* Category & Status */}
              <div className="space-y-4" data-id="element-136">
                <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-137">
                  <Star className="h-4 w-4 text-gray-400" data-id="element-138" />
                  Category & Status
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-139">
                  <div data-id="element-140">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-141">
                      Category
                    </label>
                    <select value={formData.category} onChange={e => setFormData(prev => ({
                    ...prev,
                    category: e.target.value
                  }))} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" data-id="element-142">
                      <option value="" data-id="element-143">No Category (Regular Contact)</option>
                      {(currentWorkspace.contactCategories || []).map(cat => <option key={cat} value={cat} data-id="element-144">
                          {cat}
                        </option>)}
                    </select>
                    <p className="mt-1 text-xs text-gray-500" data-id="element-145">
                      Contacts with a category are marked as high-profile
                    </p>
                  </div>

                  <div data-id="element-146">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-147">
                      Opt-in Status
                    </label>
                    <select value={formData.optInStatus} onChange={e => setFormData(prev => ({
                    ...prev,
                    optInStatus: e.target.value as 'pending' | 'confirmed' | 'unsubscribed'
                  }))} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" data-id="element-148">
                      <option value="pending" data-id="element-149">⏳ Pending</option>
                      <option value="confirmed" data-id="element-150">✓ Confirmed</option>
                      <option value="unsubscribed" data-id="element-151">✗ Unsubscribed</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500" data-id="element-152">
                      Only confirmed contacts can receive campaigns
                    </p>
                  </div>

                  <div data-id="element-153">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-154">
                      Source
                    </label>
                    <select value={formData.source} onChange={e => setFormData(prev => ({
                    ...prev,
                    source: e.target.value as 'form' | 'import' | 'manual'
                  }))} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" data-id="element-155">
                      <option value="manual" data-id="element-156">Manual Entry</option>
                      <option value="form" data-id="element-157">Form Submission</option>
                      <option value="import" data-id="element-158">Import</option>
                    </select>
                  </div>

                  <div data-id="element-159">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-160">
                      <span className="flex items-center gap-1" data-id="element-161">
                        <MapPin className="h-3 w-3" data-id="element-162" />
                        Sub-Region
                      </span>
                    </label>
                    <select value={formData.subRegion} onChange={e => setFormData(prev => ({
                    ...prev,
                    subRegion: e.target.value
                  }))} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" data-id="element-163">
                      <option value="" data-id="element-164">All Regions</option>
                      {currentWorkspace.subRegions.map(region => <option key={region} value={region} data-id="element-165">
                          {region}
                        </option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Tags */}
              <div className="space-y-3" data-id="element-166">
                <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-167">
                  <Tag className="h-4 w-4 text-gray-400" data-id="element-168" />
                  Tags
                </h3>

                {tags.length === 0 ? <p className="text-sm text-gray-500 italic" data-id="element-169">
                    No tags available in this workspace
                  </p> : <div className="flex flex-wrap gap-2" data-id="element-170">
                    {tags.map(tag => <button key={tag.id} type="button" onClick={() => handleTagToggle(tag.name)} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${formData.selectedTags.includes(tag.name) ? 'bg-blue-100 text-blue-800 border-2 border-blue-300' : 'bg-gray-100 text-gray-700 border-2 border-transparent hover:bg-gray-200'}`} data-id="element-171">
                        <Tag className="h-3 w-3" data-id="element-172" />
                        {tag.name}
                      </button>)}
                  </div>}
              </div>

              {/* Subscribed Lists */}
              <div className="space-y-3" data-id="element-173">
                <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-174">
                  <List className="h-4 w-4 text-gray-400" data-id="element-175" />
                  Subscribed Mailing Lists
                </h3>

                {newsletters.length === 0 ? <p className="text-sm text-gray-500 italic" data-id="element-176">
                    No mailing lists available
                  </p> : <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-3" data-id="element-177">
                    {newsletters.map(list => <label key={list.id} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded" data-id="element-178">
                        <input type="checkbox" checked={formData.subscribedLists.includes(list.id)} onChange={() => handleListToggle(list.id)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-179" />
                        <div className="flex-1 min-w-0" data-id="element-180">
                          <p className="text-sm font-medium text-gray-900 truncate" data-id="element-181">
                            {list.name}
                          </p>
                          <p className="text-xs text-gray-500 truncate" data-id="element-182">
                            {list.description}
                          </p>
                        </div>
                        <span className="text-xs text-gray-400 flex-shrink-0" data-id="element-183">
                          {list.subscriberCount.toLocaleString()} subscribers
                        </span>
                      </label>)}
                  </div>}
              </div>

              {/* Notes */}
              <div className="space-y-3" data-id="element-184">
                <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-185">
                  <FileText className="h-4 w-4 text-gray-400" data-id="element-186" />
                  Notes
                </h3>
                <textarea value={formData.notes} onChange={e => setFormData(prev => ({
                ...prev,
                notes: e.target.value
              }))} rows={3} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" placeholder="Add any notes about this contact..." data-id="element-187" />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0" data-id="element-188">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap" data-id="element-189">
                Cancel
              </button>
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap" data-id="element-190">
                {isEditing ? 'Save Changes' : 'Create Contact'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>;
}